import { useEffect, useRef, useState } from 'preact/hooks';
import { post } from '../api';
import { Button, Card, Pill, toast, useAction } from '../ui';
import { applyEdits, type EditResult, type StoryEdit } from './CastView';

interface Turn {
	role: 'user' | 'assistant';
	content: string;
	/** The action the decision model picked for this request. */
	action?: string | null;
	/** A proposal from the assistant: checked against the setup, waiting for the composer. */
	edits?: StoryEdit[];
	results?: EditResult[];
	state?: 'proposed' | 'applied' | 'dismissed';
}

const ACTION: Record<string, string> = {
	add_character: 'Add a character',
	remove_character: 'Remove a character',
	change_character: 'Change a character',
	add_location: 'Add a location',
	remove_location: 'Remove a location',
	connect: 'Connect characters to a location',
};

const SECTION: Record<EditResult['section'], string> = { characters: 'Characters', locations: 'Locations', characterLocations: 'Timeline' };

// Chats survive switching tabs (not reloads).
const chats = new Map<string, Turn[]>();

/**
 * The composer's setup assistant: say what to add, move or remove, and the story's compiler model
 * proposes edits to the right section (characters, locations or the timeline). Nothing changes
 * until the composer applies a proposal, and the server checks it again then.
 */
export function SetupAssistant(p: { storyId: string; locked: boolean; applied: () => void }) {
	const [turns, setTurns] = useState<Turn[]>(chats.get(p.storyId) ?? []);
	const [text, setText] = useState('');
	const log = useRef<HTMLDivElement>(null);
	useEffect(() => {
		chats.set(p.storyId, turns);
		log.current?.scrollTo({ top: log.current.scrollHeight });
	}, [turns]);
	const [send, sending] = useAction(async () => {
		const ask = text.trim();
		if (!ask) return;
		const next: Turn[] = [...turns, { role: 'user', content: ask }];
		setTurns(next);
		setText('');
		try {
			const r = await post(`/api/stories/${p.storyId}/assist`, { chat: next.map((t) => ({ role: t.role, content: t.content })) });
			setTurns([...next, { role: 'assistant', content: r.reply, action: r.action, edits: r.edits, results: r.results, state: r.edits?.length ? 'proposed' : undefined }]);
		} catch (e) {
			setTurns(turns);
			setText(ask);
			throw e;
		}
	});
	const mark = (i: number, state: Turn['state']) => setTurns((ts) => ts.map((t, n) => (n === i ? { ...t, state } : t)));
	const [apply, applying] = useAction(async (i: number) => {
		const r = await applyEdits(p.storyId, turns[i].edits ?? []);
		toast(r.map((x) => x.message).join(' '), 'ok');
		mark(i, 'applied');
		p.applied();
	});
	return (
		<Card title="Setup assistant" class="assist-card">
			<p class="small muted">Add or remove characters and locations, change a character's tier, connect characters to locations, change inventories, or set what a character needs before they can move on. The assistant proposes changes; you apply them.</p>
			<div class="assist-log" ref={log} aria-live="polite">
				{turns.length ? null : <p class="small muted">e.g. “Add a secondary character called Old Mira, a ferrywoman”, “Give Luke 20 credits” or “Luke needs the lightsaber before he goes to Mos Eisley”.</p>}
				{turns.map((t, i) => (
					<div key={i} class={`assist-msg ${t.role}`}>
						{t.action ? <p class="small muted">{ACTION[t.action] ?? t.action}</p> : null}
						<p>{t.content}</p>
						{t.results?.length ? (
							<ul class="assist-edits small">
								{t.results.map((r, n) => (
									<li key={n}>
										<Pill tone={r.section === 'characters' ? 'accent' : r.section === 'locations' ? 'ok' : 'muted'}>{SECTION[r.section]}</Pill> {r.message}
									</li>
								))}
							</ul>
						) : null}
						{t.state === 'proposed' ? (
							<div class="row">
								<Button small onClick={() => apply(i)} busy={applying} disabled={p.locked}>
									Apply
								</Button>
								<Button small variant="ghost" onClick={() => mark(i, 'dismissed')}>
									Dismiss
								</Button>
							</div>
						) : t.state ? (
							<p class="small muted">{t.state === 'applied' ? '✓ Applied' : 'Dismissed'}</p>
						) : null}
					</div>
				))}
				{sending ? (
					<div class="assist-msg assistant">
						<p class="muted">
							<span class="spinner" aria-hidden="true" /> Thinking…
						</p>
					</div>
				) : null}
			</div>
			<form
				class="assist-input"
				onSubmit={(e) => {
					e.preventDefault();
					send();
				}}
			>
				<textarea
					rows={2}
					value={text}
					placeholder="What would you like to change?"
					aria-label="Message the setup assistant"
					onInput={(e) => setText(e.currentTarget.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							send();
						}
					}}
				/>
				<Button type="submit" small busy={sending} disabled={!text.trim()}>
					Send
				</Button>
			</form>
			{turns.length ? (
				<button class="linkish small" onClick={() => setTurns([])}>
					Clear chat
				</button>
			) : null}
		</Card>
	);
}
